/**
 * Authentication Service
 * Handles user authentication, token management, and profile operations
 */

import { apiClient, tokenManager } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/config/api.config';

// Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
}

// Role types - synced with backend
export type UserRole =
  // Platform roles (OWLY team)
  | 'owly_admin'    // Platform administrator
  | 'owly_support'  // Platform support staff
  | 'owly_agency'   // External agency with assigned projects
  | 'owly_3d'       // 3D content creator
  | 'owly_reviewer' // Platform reviewer
  // Tenant roles (Client companies)
  | 'admin'
  | 'manager'
  | 'project_manager'
  | 'sales_leader'
  | 'sales_advisor'
  | 'sales' // Deprecated - usar sales_advisor
  | 'marketing'
  | 'support'
  | 'reviewer'; // External reviewer with limited access

/**
 * User Type - Distinguishes between OWLY platform users and tenant (client) users
 * - platform: OWLY team members (internal platform administrators)
 * - tenant: Client company users (external CRM users)
 */
export type UserType = 'platform' | 'tenant';

// Map role to URL slug
export type RoleSlug = UserRole;

export const ROLE_URL_MAP: Record<UserRole, RoleSlug> = {
  // Platform roles use 'admin' slug
  owly_admin: 'admin',
  owly_support: 'admin',
  owly_agency: 'admin',
  owly_3d: 'admin',
  owly_reviewer: 'admin',
  // Tenant roles
  admin: 'admin',
  manager: 'manager',
  project_manager: 'project_manager',
  sales_leader: 'sales_leader',
  sales_advisor: 'sales_advisor',
  sales: 'sales_advisor', // Redirect legacy
  marketing: 'marketing',
  support: 'support',
  reviewer: 'reviewer',
};

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: UserRole;
  user_type: UserType; // platform (OWLY) or tenant (client)
  company?: string | null; // UUID de la compañía
  company_data?: {
    id: string;
    name: string;
    plan: string;
    logo?: string | null;
  } | null;
  is_active: boolean;
  is_superuser: boolean;
  is_staff: boolean;
  is_manager: boolean;
  phone?: string;
  avatar?: string;

  // Project assignments
  assigned_projects?: string[]; // Array of project UUIDs
  assigned_project_ids?: string[]; // Array of project IDs (alias)

  must_change_password?: boolean;
  performance_metrics?: any;
  settings?: any;
  signature?: string;
  social_media?: any;
  can_approve_quotes?: boolean;
  created_at: string;
  updated_at: string;
  date_joined?: string;
  last_login?: string;
}

export interface ChangePasswordData {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  avatar?: string;
}

/**
 * Authentication Service
 */
export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials,
      { skipAuth: true }
    );

    console.log('🔐 [authService] Login response:', response);
    console.log('👤 [authService] User data:', response.user);

    // Store tokens and user data
    tokenManager.setTokens(response.access, response.refresh);
    tokenManager.setUser(response.user);

    console.log('✅ [authService] User saved to localStorage');
    console.log('📦 [authService] Verificando localStorage:', localStorage.getItem('user'));

    return response;
  },

  /**
   * Logout - clear local storage
   */
  logout(): void {
    tokenManager.clearTokens();
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<{ access: string }> {
    const response = await apiClient.post<{ access: string }>(
      API_ENDPOINTS.AUTH.REFRESH,
      { refresh: refreshToken },
      { skipAuth: true }
    );
    
    tokenManager.setTokens(response.access);
    return response;
  },

  /**
   * Verify token
   */
  async verifyToken(token: string): Promise<void> {
    await apiClient.post(
      API_ENDPOINTS.AUTH.VERIFY,
      { token },
      { skipAuth: true }
    );
  },

  /**
   * Get current user profile
   */
  async getProfile(): Promise<User> {
    const user = await apiClient.get<User>(API_ENDPOINTS.AUTH.PROFILE);
    tokenManager.setUser(user);
    return user;
  },

  /**
   * Update current user profile
   */
  async updateProfile(data: UpdateProfileData): Promise<User> {
    const user = await apiClient.patch<User>(API_ENDPOINTS.AUTH.PROFILE, data);
    tokenManager.setUser(user);
    return user;
  },

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      data
    );
  },

  /**
   * Cambio obligatorio de contraseña en primer login
   */
  async forceChangePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<{ message: string; must_change_password: boolean }> {
    return apiClient.post<{ message: string; must_change_password: boolean }>(
      `${API_ENDPOINTS.USERS.BASE}force-change-password/`,
      data
    );
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!tokenManager.getAccessToken();
  },

  /**
   * Get current user from storage
   */
  getCurrentUser(): User | null {
    return tokenManager.getUser();
  },
};

